const AUTH_TOKEN_KEY = 'token';
const AUTH_USER_KEY = 'user';

/**
 * Run once at app startup: migrate legacy session auth and normalize stored token.
 */
export function initAuthStorage() {
  try {
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) {
      const sessionToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
      if (sessionToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, sessionToken);
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
      }
    }
    if (!localStorage.getItem(AUTH_USER_KEY)) {
      const sessionUser = sessionStorage.getItem(AUTH_USER_KEY);
      if (sessionUser) {
        localStorage.setItem(AUTH_USER_KEY, sessionUser);
        sessionStorage.removeItem(AUTH_USER_KEY);
      }
    }

    const raw = localStorage.getItem(AUTH_TOKEN_KEY);
    if (raw) {
      const clean = raw.replace(/^Bearer\s+/i, '').trim();
      if (clean && clean !== raw) {
        localStorage.setItem(AUTH_TOKEN_KEY, clean);
      }
    }
  } catch {
    /* storage blocked (private mode, etc.) */
  }
}

/**
 * Get the Authorization header with Bearer token
 * Handles cases where token might already have "Bearer " prefix
 * @returns {Object} Header object with Authorization
 */
export const getAuthHeader = () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    if (!token) {
        return {};
    }

    // Remove "Bearer " if it exists to avoid double prefix
    const cleanToken = token.replace(/^Bearer\s+/i, '').trim();

    return {
        'Authorization': `Bearer ${cleanToken}`,
        'Content-Type': 'application/json'
    };
};

/**
 * Get the raw token (without Bearer prefix)
 */
export const getToken = () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return null;
    return token.replace(/^Bearer\s+/i, '').trim();
};

function readBearer(headers) {
    if (!headers) return null;
    let value = null;
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
        value = headers.get('Authorization');
    } else if (Array.isArray(headers)) {
        const pair = headers.find(([k]) => String(k).toLowerCase() === 'authorization');
        value = pair ? pair[1] : null;
    } else if (typeof headers === 'object') {
        const key = Object.keys(headers).find((k) => k.toLowerCase() === 'authorization');
        value = key ? headers[key] : null;
    }
    if (!value) return null;
    return String(value).replace(/^Bearer\s+/i, '').trim() || null;
}

/**
 * When the API rejects the *current* session token (expired / user deactivated), clear the
 * session and send the user to login instead of leaving a half-broken dashboard.
 */
export function installSessionExpiryGuard(apiBaseUrl) {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function' || window.__traktSessionGuard) return;
    window.__traktSessionGuard = true;
    const nativeFetch = window.fetch.bind(window);
    let redirecting = false;

    window.fetch = async (input, init) => {
        const res = await nativeFetch(input, init);
        if (res.status !== 401 || redirecting) return res;

        const url = typeof input === 'string' ? input : input?.url || String(input || '');
        if (!apiBaseUrl || !url.startsWith(apiBaseUrl)) return res;

        const sent = readBearer(init?.headers) || (typeof Request !== 'undefined' && input instanceof Request ? readBearer(input.headers) : null);
        const current = getToken();
        if (!sent || !current || sent !== current) return res;

        redirecting = true;
        try {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
            localStorage.removeItem('companyFeatures');
        } catch {
            /* storage blocked */
        }
        if (!window.location.pathname.startsWith('/login')) {
            window.location.assign('/login?session=expired');
        }
        return res;
    };
}

/** Nest transform interceptor wraps non-list responses as { success, data }. */
export const unwrapApiPayload = (payload) => {
    if (payload && typeof payload === 'object' && payload.success === true && 'data' in payload) {
        return payload.data;
    }
    return payload;
};
